import type { NotificationPublic, ReadNotificationsType, UnreadNotificationsCount } from '@repo/types/notifications';
import type { InfiniteData, QueryKey } from '@tanstack/react-query';

import { NotificationType } from '@repo/types/enums/notificationType';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { NotificationKey } from './keys';
import { NotificationService } from './notificationService';

/**
 * Extracts notification IDs from the read notifications data.
 *
 * @param data - Array of notifications to mark as read
 * @returns Array of notification IDs
 */
function extractNotificationIds(data: ReadNotificationsType[]): number[] {
  return data.map((item) => item.id);
}

/**
 * Marks specified notifications as read in the cached data.
 *
 * @param pages - Current notification pages from cache
 * @param notificationIds - IDs of notifications to mark as read
 * @returns Updated notification pages with read status applied
 */
function markNotificationsAsRead(pages: NotificationPublic[][], notificationIds: number[]): NotificationPublic[][] {
  return pages.map((page) =>
    page.map((notification) => ({
      ...notification,
      read: notificationIds.includes(notification.id) ? true : notification.read,
    }))
  );
}

/**
 * Calculates the count reduction for each notification type.
 * For chat notifications, counts all unread notifications with the same entity.
 * For other types, simply increments by 1.
 *
 * @param data - Notifications being marked as read
 * @param allNotifications - All cached notifications for reference
 * @returns Record mapping notification types to their count reductions
 */
function calculateCountReductionsByType(data: ReadNotificationsType[], allNotifications: NotificationPublic[]): Record<string, number> {
  return data.reduce(
    (accumulator, item) => {
      const currentCount = accumulator[item.type] || 0;

      if (item.type === NotificationType.Chat && item.entityId) {
        // For chat notifications, count all unread notifications with the same entityId
        const unreadChatCount = allNotifications.filter((notification) => notification.type === NotificationType.Chat && notification.entityId === item.entityId && !notification.read).length;
        accumulator[item.type] = currentCount + unreadChatCount;
      } else {
        accumulator[item.type] = currentCount + 1;
      }

      return accumulator;
    },
    {} as Record<string, number>
  );
}

/**
 * Updates unread notification counts by subtracting the reductions.
 *
 * @param currentCounts - Current unread counts from cache
 * @param reductionsByType - Count reductions for each notification type
 * @returns Updated unread notification counts
 */
function updateUnreadCounts(currentCounts: UnreadNotificationsCount[], reductionsByType: Record<string, number>): UnreadNotificationsCount[] {
  return currentCounts.map((count) => ({
    ...count,
    count: String(Math.max(0, Number(count.count) - (reductionsByType[count.type] || 0))),
  }));
}

/**
 * Hook for marking specific notifications as read.
 *
 * This mutation performs optimistic updates to:
 * - Mark the specified notifications as read in the infinite list
 * - Decrement the unread count for each notification type
 *
 * For chat notifications, it handles the special case where marking one
 * notification as read should also update the count for all unread
 * notifications with the same entity ID.
 *
 * @returns React Query mutation object for marking notifications as read
 *
 * @example
 * // Mark a single notification as read
 * const mutation = usePostReadNotifications();
 * mutation.mutate([notificationData]);
 */
export function usePostReadNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReadNotificationsType[]) => NotificationService.readNotifications(extractNotificationIds(data)).then((response) => response.data),

    onMutate: async (data) => {
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: [NotificationKey.NOTIFICATIONS_INFINITE],
          exact: false,
        }),
        queryClient.cancelQueries({
          queryKey: [NotificationKey.UNREAD_NOTIFICATIONS_COUNT],
          exact: false,
        }),
      ]);

      // Capture notifications state BEFORE updating to correctly count unread notifications
      const cachedNotificationsEntries = queryClient.getQueriesData<InfiniteData<NotificationPublic[]>>({
        queryKey: [NotificationKey.NOTIFICATIONS_INFINITE],
      });

      const allNotifications = cachedNotificationsEntries.flatMap(([, cached]) => cached?.pages.flat() ?? []);
      const notificationIds = extractNotificationIds(data);

      // Optimistically update all notifications lists (for every filter variant)
      cachedNotificationsEntries.forEach(([queryKey]) => {
        queryClient.setQueryData<InfiniteData<NotificationPublic[]> | undefined>(queryKey as QueryKey, (currentData) => {
          if (!currentData) {
            return undefined;
          }

          return {
            ...currentData,
            pages: markNotificationsAsRead(currentData.pages, notificationIds),
          };
        });
      });

      // Optimistically update the unread counts
      queryClient.setQueryData<UnreadNotificationsCount[] | undefined>([NotificationKey.UNREAD_NOTIFICATIONS_COUNT], (currentCounts) => {
        if (!currentCounts) {
          return undefined;
        }

        const reductionsByType = calculateCountReductionsByType(data, allNotifications);

        return updateUnreadCounts(currentCounts, reductionsByType);
      });
    },
  });
}
